import { supabase } from './supabase';
import type { Campaign, CampaignStats, CampaignTarget } from '../types';
import type { FilterState } from '../constants';
import { COST_PER_LETTER_PENCE, DEFAULT_TEMPLATE_ID } from '../constants';

export interface CreateCampaignArgs {
  name: string;
  filters: FilterState;
  polygon?: GeoJSON.Polygon | null;
  postcodes?: string[];
  templateId?: string;
  maxTargets?: number;
}

export async function createCampaign(args: CreateCampaignArgs): Promise<string> {
  const { data, error } = await supabase.rpc('doorsteps_create_campaign', {
    p_name: args.name,
    p_template_id: args.templateId ?? DEFAULT_TEMPLATE_ID,
    p_filters: args.filters,
    p_polygon: args.polygon ?? null,
    p_postcodes: args.postcodes && args.postcodes.length ? args.postcodes : null,
    p_max_targets: args.maxTargets ?? 5000,
  });
  if (error) throw error;
  return data as string;
}

export async function listCampaigns(agentId: string): Promise<Campaign[]> {
  const { data, error } = await supabase
    .from('doorsteps_campaigns')
    .select('*')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Campaign[];
}

export async function getCampaign(id: string): Promise<Campaign | null> {
  const { data, error } = await supabase
    .from('doorsteps_campaigns')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  return data as Campaign | null;
}

export async function getCampaignTargets(campaignId: string): Promise<CampaignTarget[]> {
  const { data, error } = await supabase
    .from('doorsteps_campaign_targets')
    .select('*')
    .eq('campaign_id', campaignId)
    .order('created_at', { ascending: false })
    .limit(2000);
  if (error) throw error;
  return (data || []) as CampaignTarget[];
}

export async function getCampaignStats(campaignId: string): Promise<CampaignStats | null> {
  const campaign = await getCampaign(campaignId);
  if (!campaign) return null;

  const [{ count: lettersSent }, { count: qrScansCount }, { count: formsCount }] = await Promise.all([
    supabase
      .from('doorsteps_campaign_targets')
      .select('id', { count: 'exact', head: true })
      .eq('campaign_id', campaignId)
      .in('status', ['posted', 'delivered']),
    supabase
      .from('doorsteps_activities')
      .select('id', { count: 'exact', head: true })
      .eq('campaign_id', campaignId)
      .eq('type', 'qr_scanned'),
    supabase
      .from('doorsteps_activities')
      .select('id', { count: 'exact', head: true })
      .eq('campaign_id', campaignId)
      .eq('type', 'form_submitted'),
  ]);

  // valuations_booked = homeowners that touched this campaign and reached that stage
  const { count: valuationsCount } = await supabase
    .from('doorsteps_homeowner_records')
    .select('id', { count: 'exact', head: true })
    .eq('source_campaign_id', campaignId)
    .in('pipeline_stage', ['valuation_booked', 'instructed']);

  return {
    campaign,
    letters_sent: lettersSent ?? 0,
    qr_scans: qrScansCount ?? 0,
    form_submissions: formsCount ?? 0,
    valuations_booked: valuationsCount ?? 0,
  };
}

import { getDoorstepsLetterRatePence, type DoorstepsTier } from '@rehub/pricing';

/**
 * Cost in pence for a campaign of `targetCount` letters.
 * Tier-aware: pass the agent's current tier (defaults to legacy COST_PER_LETTER_PENCE
 * when not supplied for backwards compatibility).
 */
export function estimateCampaignCostPence(targetCount: number, tier?: DoorstepsTier | null): number {
  if (tier) return targetCount * getDoorstepsLetterRatePence(tier);
  return targetCount * COST_PER_LETTER_PENCE;
}

export function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

/**
 * Send the campaign through the create-campaign edge function. The function
 * is responsible for calling Stannp, materialising tracking, and flipping
 * status to 'sending' / 'sent'.
 */
export async function dispatchCampaign(campaignId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('doorsteps-dispatch-campaign', {
    body: { campaign_id: campaignId },
  });
  if (error) throw error;
  if (data?.success === false) throw new Error(data?.error || 'Dispatch failed');
}
